import { Button, PageHeader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPatientInvoiceServicePrice } from '@common/interfaces/patientInvoiceServicePrice';
import type { IPatientInvoiceServicePriceAddEditValidated } from '@common/validation/patientInvoiceServicePrice';
import { ValidationErrorCode } from '@common/enums';
import { DangerAlertWhenErrorCode } from '@frontend/components/elements/common';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import { PatientInvoiceServicePriceAddEdit } from './PatientInvoiceServicePriceAddEdit';
import { PatientInvoiceServicePricesList } from './PatientInvoiceServicePricesList';

interface IPatientInvoiceServicePriceProps {
    serviceId: string;
}

const baseUrl = '/api/patientInvoiceServicePrices';

export function PatientInvoiceServicePrices(props: IPatientInvoiceServicePriceProps) {
    const abortableFetch = useAbortableFetch();
    const [overlapError, setOverlapError] = useState<string>();
    const [isEditing, setIsEditing] = useState(false);
    const [patientInvoiceServicePrices, setPatientInvoiceServicePrices] = useState<IPatientInvoiceServicePrice[]>([]);
    const [editablePatientInvoiceServicePrice, setEditablePatientInvoiceServicePrice] = useState<
        Partial<IPatientInvoiceServicePrice>
    >({});

    useEffect(() => {
        getAll();
    }, []);

    return (
        <PageTemplate>
            <PageHeader
                action={
                    !isEditing && (
                        <Button onClick={handleClickAdd}>
                            {translateKey('patientInvoiceServicePrices.addPricePeriod')}
                        </Button>
                    )
                }
                title={translateKey('patientInvoiceServicePrices.patientInvoicing')}
            />
            <DangerAlertWhenErrorCode errorCode={overlapError} />

            {isEditing ? (
                <PatientInvoiceServicePriceAddEdit
                    onCancel={handleCancel}
                    onChange={handleChange}
                    onSave={(patientInvoiceServicePrice: IPatientInvoiceServicePriceAddEditValidated) => {
                        if (patientInvoiceServicePrice.id) {
                            update(patientInvoiceServicePrice);
                        } else {
                            create(patientInvoiceServicePrice);
                        }
                    }}
                    patientInvoiceServicePrice={editablePatientInvoiceServicePrice}
                />
            ) : (
                <PatientInvoiceServicePricesList
                    onClickEdit={handleClickEdit}
                    onRemove={remove}
                    patientInvoiceServicePrices={patientInvoiceServicePrices}
                    {...props}
                />
            )}
        </PageTemplate>
    );

    function handleClickAdd() {
        setEditablePatientInvoiceServicePrice({
            serviceId: props.serviceId,
        });
        setIsEditing(true);
    }

    function handleClickEdit(id: string) {
        setEditablePatientInvoiceServicePrice(patientInvoiceServicePrices.find((price) => price.id === id) || {});
        setIsEditing(true);
    }

    function handleCancel() {
        resetEditState();
    }

    function handleChange(key: keyof IPatientInvoiceServicePrice, value: string | number | Date | null) {
        setEditablePatientInvoiceServicePrice({
            ...editablePatientInvoiceServicePrice,
            [key]: value,
        } as IPatientInvoiceServicePrice);
    }

    async function create(patientInvoiceServicePrice: IPatientInvoiceServicePriceAddEditValidated) {
        if (!editablePatientInvoiceServicePrice) {
            return;
        }

        const result = await abortableFetch.fetchJson(baseUrl, 'POST', patientInvoiceServicePrice, [
            ValidationErrorCode.OVERLAPPING_CONFIG,
        ]);

        if (result.errorCode) {
            setOverlapError(result.errorCode);
        } else {
            await resetEditState(true);
        }
    }

    async function update(patientInvoiceServicePrice: IPatientInvoiceServicePriceAddEditValidated) {
        const url = `${baseUrl}/${patientInvoiceServicePrice.id}`;

        const result = await abortableFetch.fetchJson(url, 'POST', patientInvoiceServicePrice, [
            ValidationErrorCode.OVERLAPPING_CONFIG,
        ]);

        if (result.errorCode) {
            setOverlapError(result.errorCode);
        } else {
            await resetEditState(true);
        }
    }

    async function remove(id: string) {
        const url = `${baseUrl}/${id}`;
        await abortableFetch.deleteJson(url);
        await resetEditState(true);
    }

    async function getAll() {
        const url = `${baseUrl}?serviceId=${props.serviceId}`;
        const items = (await abortableFetch.getJson(url))?.json?.items;
        setPatientInvoiceServicePrices([...items]);
    }

    async function resetEditState(refetch: boolean = false) {
        if (refetch) {
            await getAll();
        }
        setEditablePatientInvoiceServicePrice({});
        setIsEditing(false);
        setOverlapError(undefined);
    }
}
